import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, ApiError, type TaskInput, type TaskView } from '../lib/api'

const PRIORITY_TONE = {
  urgent: 'bad',
  high: 'warn',
  normal: undefined,
  low: undefined,
} as const

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'

/** `<input type="datetime-local">` wants "YYYY-MM-DDTHH:mm" in the browser's own timezone — built
 *  from local getters, not toISOString(), which would silently shift the value to UTC. */
export function toLocalDatetimeInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function tomorrowAt(hour: number): Date {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  d.setHours(hour, 0, 0, 0)
  return d
}

/** One click instead of five clicks in a native date/time picker — the presets this category's
 *  other tools all offer (in 1 hour / tomorrow / next week) rather than Wami being the one
 *  screen where a due date has to be typed by hand. */
const DUE_PRESETS: { label: string; at: () => Date }[] = [
  { label: 'In 1 hour', at: () => new Date(Date.now() + 60 * 60_000) },
  { label: 'In 3 hours', at: () => new Date(Date.now() + 3 * 60 * 60_000) },
  { label: 'Tomorrow 9am', at: () => tomorrowAt(9) },
  { label: 'Next week', at: () => new Date(Date.now() + 7 * 86_400_000) },
]

export function DatePresetChips({ onPick }: { onPick: (value: string) => void }) {
  return (
    <div className="row gap-1 wrap" style={{ marginTop: '4px' }}>
      {DUE_PRESETS.map((preset) => (
        <button
          key={preset.label}
          type="button"
          className="cad-pill"
          onClick={() => onPick(toLocalDatetimeInput(preset.at()))}
        >
          {preset.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Anything that started as a message but needs a person and a deadline attached to it.
 *
 * A task tied to a conversation only shows up here for someone who can already read that
 * conversation — the same label scoping the inbox uses — so this list is never a way to see
 * more of the business than a restricted member is otherwise granted.
 */
export function Tasks() {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState('open')
  const [assignee, setAssignee] = useState('')
  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [selectMode, setSelectMode] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkOutcome, setBulkOutcome] = useState<string | null>(null)

  const tasksQuery = useQuery({
    queryKey: ['tasks', status, assignee],
    queryFn: () => api.tasks({ status: status || undefined, assignee: assignee || undefined }),
    retry: false,
  })

  const team = useQuery({ queryKey: ['team'], queryFn: api.team, retry: false })
  const nameFor = (userId: string | null) =>
    userId ? (team.data?.members.find((m) => m.userId === userId)?.name ?? 'Former teammate') : null

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['tasks'] })

  const complete = useMutation({ mutationFn: (id: string) => api.completeTask(id), onSuccess: refresh })
  const reopen = useMutation({ mutationFn: (id: string) => api.reopenTask(id), onSuccess: refresh })
  const remove = useMutation({ mutationFn: (id: string) => api.deleteTask(id), onSuccess: refresh })

  const toggleSelected = (id: string) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // One call per selected task, the same mutations the per-row buttons already use — a bulk
  // route would need to re-derive the same "complete only touches open, reopen only touches
  // done" eligibility this already gets for free by just calling each row's own action.
  const bulkRun = useMutation({
    mutationFn: async (action: 'complete' | 'reopen' | 'delete') => {
      const ids = [...selected]
      const call = action === 'complete' ? api.completeTask : action === 'reopen' ? api.reopenTask : api.deleteTask
      const results = await Promise.all(
        ids.map((id) =>
          call(id)
            .then(() => ({ id, ok: true as const }))
            .catch((err) => ({ id, ok: false as const, error: err instanceof ApiError ? err.message : 'Failed' })),
        ),
      )
      return { action, results }
    },
    onSuccess: ({ action, results }) => {
      const failed = results.filter((r) => !r.ok)
      const verb = action === 'complete' ? 'Completed' : action === 'reopen' ? 'Reopened' : 'Deleted'
      setBulkOutcome(
        failed.length === 0
          ? `${verb} ${results.length} task${results.length === 1 ? '' : 's'}.`
          : `${verb} ${results.length - failed.length} of ${results.length} — some did not go through.`,
      )
      setSelected(new Set())
      void refresh()
    },
  })

  if (tasksQuery.isPending) return <div className="cad-card" style={{ height: '200px' }} />
  if (tasksQuery.isError) {
    const message = tasksQuery.error instanceof ApiError ? tasksQuery.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { tasks, counts, scoped } = tasksQuery.data!

  return (
    <>
      <header className="stack gap-1">
        <span className="cad-eyebrow">Tasks</span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">{counts.open} open</h1>
          <div className="row gap-2 wrap">
            {counts.overdue > 0 && <span className="cad-pill" data-tone="bad">{counts.overdue} overdue</span>}
            {counts.dueSoon > 0 && <span className="cad-pill" data-tone="warn">{counts.dueSoon} due within a day</span>}
            {tasks.length > 0 && (
              <button
                type="button"
                className="cad-btn"
                data-variant={selectMode ? undefined : 'quiet'}
                onClick={() => {
                  setSelectMode((v) => !v)
                  setSelected(new Set())
                  setBulkOutcome(null)
                }}
              >
                {selectMode ? 'Done selecting' : 'Select'}
              </button>
            )}
            {!creating && (
              <button
                type="button"
                className="cad-btn"
                onClick={() => {
                  setEditingId(null)
                  setCreating(true)
                }}
              >
                New task
              </button>
            )}
          </div>
        </div>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Turn a chat or just a follow-up into something with an owner and a deadline — open a
          conversation's Tasks tab in the inbox to link one to it, or add a standalone one here.
        </p>
        {scoped && (
          <p className="cad-note">
            You only see tasks tied to a conversation under a label you have been granted, plus
            any standalone task.
          </p>
        )}
        {(complete.error || reopen.error || remove.error) && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {(() => {
              const err = complete.error ?? reopen.error ?? remove.error
              return err instanceof ApiError || err instanceof Error ? err.message : 'That did not go through.'
            })()}
          </p>
        )}
      </header>

      <div className="row gap-2 wrap">
        {(['open', 'done', 'cancelled', 'all'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className="cad-pill"
            data-tone={status === value ? 'accent' : undefined}
            onClick={() => setStatus(value)}
          >
            {value === 'all' ? 'All' : value[0].toUpperCase() + value.slice(1)}
          </button>
        ))}
        <span className="cad-muted" style={{ margin: '0 4px' }}>·</span>
        <button
          type="button"
          className="cad-pill"
          data-tone={assignee === '' ? 'accent' : undefined}
          onClick={() => setAssignee('')}
        >
          Everyone
        </button>
        <button
          type="button"
          className="cad-pill"
          data-tone={assignee === 'me' ? 'accent' : undefined}
          onClick={() => setAssignee('me')}
        >
          Assigned to me
        </button>
        <button
          type="button"
          className="cad-pill"
          data-tone={assignee === 'unassigned' ? 'accent' : undefined}
          onClick={() => setAssignee('unassigned')}
        >
          Unassigned
        </button>
      </div>

      {creating && (
        <TaskForm
          members={team.data?.members ?? []}
          onDone={() => {
            setCreating(false)
            void refresh()
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {editingId && (
        <TaskForm
          task={tasks.find((t) => t.id === editingId)}
          members={team.data?.members ?? []}
          onDone={() => {
            setEditingId(null)
            void refresh()
          }}
          onCancel={() => setEditingId(null)}
        />
      )}

      {selectMode && selected.size > 0 && (
        <div className="cad-card">
          <div className="stack gap-2">
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
              {selected.size} task{selected.size === 1 ? '' : 's'} selected
            </span>
            <div className="row gap-2 wrap">
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={bulkRun.isPending}
                onClick={() => bulkRun.mutate('complete')}
              >
                Complete
              </button>
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={bulkRun.isPending}
                onClick={() => bulkRun.mutate('reopen')}
              >
                Reopen
              </button>
              <button
                type="button"
                className="cad-btn"
                data-variant="text"
                disabled={bulkRun.isPending}
                onClick={() => {
                  if (confirm(`Delete ${selected.size} task${selected.size === 1 ? '' : 's'}?\n\nThey disappear for everyone they were assigned to. This cannot be undone.`)) bulkRun.mutate('delete')
                }}
              >
                Delete
              </button>
            </div>
            {bulkOutcome && <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>{bulkOutcome}</span>}
          </div>
        </div>
      )}

      <section className="cad-card">
        {tasks.length === 0 ? (
          <p className="cad-muted">
            No tasks yet. Tasks are follow-ups with an owner and a due date &mdash; create one here, or
            from the Tasks panel inside any chat so it stays linked to that conversation.
          </p>
        ) : (
          <div className="cad-grid-frame">
            <table className="cad-table">
              <thead>
                <tr>
                  {selectMode && <th />}
                  <th>Task</th>
                  <th>Priority</th>
                  <th>Assignee</th>
                  <th>Due</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tasks.map((task: TaskView) => (
                  <tr key={task.id}>
                    {selectMode && (
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Select ${task.title}`}
                          checked={selected.has(task.id)}
                          onChange={() => toggleSelected(task.id)}
                        />
                      </td>
                    )}
                    <td>
                      <div className="stack gap-1">
                        <span style={{ fontWeight: 500 }}>{task.title}</span>
                        {task.notes && (
                          <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>{task.notes}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="cad-pill" data-tone={PRIORITY_TONE[task.priority]}>{task.priority}</span>
                    </td>
                    <td className="cad-muted">{nameFor(task.assigneeUserId) ?? 'Unassigned'}</td>
                    <td>
                      {task.dueAt ? (
                        <span className={task.overdue ? undefined : 'cad-muted'}>
                          {task.overdue && <span className="cad-pill" data-tone="bad" style={{ marginRight: '6px' }}>overdue</span>}
                          {fmt(task.dueAt)}
                        </span>
                      ) : (
                        <span className="cad-muted">—</span>
                      )}
                    </td>
                    <td>
                      <span className="cad-pill" data-tone={task.status === 'done' ? 'good' : undefined}>
                        {task.status}
                      </span>
                    </td>
                    <td className="num">
                      <div className="row gap-2 wrap" style={{ justifyContent: 'flex-end' }}>
                        {task.status === 'open' ? (
                          <button
                            type="button"
                            className="cad-btn"
                            data-variant="quiet"
                            onClick={() => complete.mutate(task.id)}
                          >
                            Complete
                          </button>
                        ) : task.status === 'done' ? (
                          <button
                            type="button"
                            className="cad-btn"
                            data-variant="quiet"
                            onClick={() => reopen.mutate(task.id)}
                          >
                            Reopen
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="cad-btn"
                          data-variant="text"
                          onClick={() => {
                            setCreating(false)
                            setEditingId(task.id)
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="cad-btn"
                          data-variant="text"
                          onClick={() => {
                            if (confirm(`Delete this task?

It disappears for everyone it was assigned to. This cannot be undone.`)) remove.mutate(task.id)
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}

export function TaskForm({
  task,
  conversationId,
  ticketId,
  messageId,
  members,
  initialTitle,
  onDone,
  onCancel,
}: {
  /** Present for an edit; absent for a fresh task. Switches the form between create and update
   *  without duplicating the field layout for what is otherwise the identical set of inputs. */
  task?: TaskView
  conversationId?: string
  ticketId?: string
  messageId?: string
  members: { userId: string; name: string }[]
  /** Quoted from the triggering message rather than left blank, so creating a task costs one
   *  click, not a re-typed summary — still freely editable before it saves. */
  initialTitle?: string
  onDone: () => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(task?.title ?? initialTitle ?? '')
  const [notes, setNotes] = useState(task?.notes ?? '')
  const [priority, setPriority] = useState<TaskInput['priority']>(task?.priority ?? 'normal')
  const [assigneeUserId, setAssigneeUserId] = useState(task?.assigneeUserId ?? '')
  const [dueAt, setDueAt] = useState(task?.dueAt ? toLocalDatetimeInput(new Date(task.dueAt)) : '')
  const [remindAt, setRemindAt] = useState(task?.remindAt ? toLocalDatetimeInput(new Date(task.remindAt)) : '')

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title,
        notes: notes || null,
        priority,
        assigneeUserId: assigneeUserId || null,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
        remindAt: remindAt ? new Date(remindAt).toISOString() : null,
      }
      return task
        ? api.updateTask(task.id, body)
        : api.createTask({ ...body, conversationId, ticketId, messageId })
    },
    onSuccess: onDone,
  })

  const error = save.error
  const message = error instanceof ApiError || error instanceof Error ? error.message : undefined

  return (
    <section className="cad-card">
      <div className="stack gap-4">
        <h2 className="cad-section-title">{task ? 'Edit task' : 'New task'}</h2>
        <div className="cad-field">
          <label htmlFor="task-title">Title</label>
          <input
            id="task-title"
            className="cad-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Call back about the delayed order"
          />
        </div>
        <div className="cad-field">
          <label htmlFor="task-notes">Notes</label>
          <textarea
            id="task-notes"
            className="cad-input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="field-grid">
          <div className="cad-field">
            <label htmlFor="task-priority">Priority</label>
            <select
              id="task-priority"
              className="cad-input"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskInput['priority'])}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <div className="cad-field">
            <label htmlFor="task-assignee">Assignee</label>
            <select
              id="task-assignee"
              className="cad-input"
              value={assigneeUserId}
              onChange={(e) => setAssigneeUserId(e.target.value)}
            >
              <option value="">Unassigned</option>
              {members.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>
          <div className="cad-field">
            <label htmlFor="task-due">Due</label>
            <input
              id="task-due"
              type="datetime-local"
              className="cad-input"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
            <DatePresetChips onPick={setDueAt} />
          </div>
          <div className="cad-field">
            <label htmlFor="task-remind">Remind at</label>
            <input
              id="task-remind"
              type="datetime-local"
              className="cad-input"
              value={remindAt}
              onChange={(e) => setRemindAt(e.target.value)}
            />
            <DatePresetChips onPick={setRemindAt} />
          </div>
        </div>

        {message && <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>{message}</p>}

        <div className="row gap-2 wrap">
          <button
            type="button"
            className="cad-btn"
            disabled={!title.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : task ? 'Save changes' : 'Create task'}
          </button>
          <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  )
}
